import { useEffect, useState } from "react";
import { FilePlus2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { DAY_LABELS } from "@/components/assignments/StandingApprovalPanel";

export const RecordAssignmentForm = ({ onCreated }) => {
  const [providers, setProviders] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ provider_id: "", client_id: "", start_date: "", note: "" });
  const [withApproval, setWithApproval] = useState(false);
  const [days, setDays] = useState([]);
  const [window, setWindow] = useState({ start_time: "", end_time: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const approvalComplete = !withApproval || (days.length > 0 && window.start_time && window.end_time && window.end_time > window.start_time);

  useEffect(() => {
    Promise.all([api.get("/providers/approved"), api.get("/providers/client-options")])
      .then(([p, c]) => { setProviders(p.data); setClients(c.data); })
      .catch((e) => setError(formatApiError(e)));
  }, []);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, standing_approval: withApproval ? { days, start_time: window.start_time, end_time: window.end_time } : null };
      const response = await api.post("/assignments/record", payload);
      setForm({ provider_id: "", client_id: "", start_date: "", note: "" });
      setWithApproval(false);
      setDays([]);
      setWindow({ start_time: "", end_time: "" });
      setError("");
      onCreated(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="record-assignment-form" data-testid="record-assignment-form" onSubmit={submit}>
      <div className="section-heading"><div><h3><FilePlus2 size={17} /> Add record-keeping assignment</h3><p>No provider notification is sent and open-position counts are unchanged.</p></div></div>
      <div className="record-assignment-grid">
        <label>Provider
          <select data-testid="record-provider-select" required value={form.provider_id} onChange={(e) => setForm({ ...form, provider_id: e.target.value })}>
            <option value="">Select provider</option>
            {providers.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select>
        </label>
        <label>Client
          <select data-testid="record-client-select" required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
            <option value="">Select client</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label>Start date <input type="date" data-testid="record-start-date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></label>
        <label>Note <input data-testid="record-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional" /></label>
      </div>
      <label className="checkbox-row"><input type="checkbox" data-testid="record-standing-toggle" checked={withApproval} onChange={(e) => setWithApproval(e.target.checked)} /> Add a standing schedule approval</label>
      {withApproval && (
        <div className="standing-approval-form">
          <p className="pairing-meta" data-testid="record-standing-instructions">Select only the days and hours being approved. Nothing is pre-filled.</p>
          <div className="standing-approval-days">
            {DAY_LABELS.map((label, index) => (
              <label key={label} className="checkbox-row"><input type="checkbox" data-testid={`record-day-${index}`} checked={days.includes(index)} onChange={() => setDays(days.includes(index) ? days.filter((d) => d !== index) : [...days, index].sort())} /> {label}</label>
            ))}
          </div>
          <div className="standing-approval-times">
            <label>From <input type="time" data-testid="record-standing-start-input" required value={window.start_time} onChange={(e) => setWindow({ ...window, start_time: e.target.value })} /></label>
            <label>To <input type="time" data-testid="record-standing-end-input" required value={window.end_time} onChange={(e) => setWindow({ ...window, end_time: e.target.value })} /></label>
          </div>
        </div>
      )}
      <div className="button-row">
        <button type="submit" className="button button-primary" data-testid="record-assignment-submit" disabled={busy || !form.provider_id || !form.client_id || !approvalComplete}>{busy ? "Saving…" : "Add record"}</button>
      </div>
      {error && <p className="form-message error-message" data-testid="record-assignment-error">{error}</p>}
    </form>
  );
};
